import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-heading font-semibold ring-offset-background transition-[var(--transition-smooth)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-hover)] hover:translate-y-[-3px] rounded-lg",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-medium)] hover:translate-y-[-2px] rounded-lg",
        outline:
          "border-2 border-primary bg-transparent text-primary hover:bg-primary hover:text-primary-foreground hover:shadow-[var(--shadow-medium)] hover:translate-y-[-2px] rounded-lg",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-medium)] hover:translate-y-[-2px] rounded-lg",
        ghost: "hover:bg-accent/10 hover:text-accent rounded-lg",
        link: "text-primary underline-offset-4 hover:underline hover:text-purple-accent",
        cta: "bg-gradient-to-r from-accent to-purple-accent text-white hover:shadow-[var(--shadow-hover)] hover:translate-y-[-3px] hover:scale-[1.02] rounded-lg font-bold",
        hero: "bg-gradient-to-r from-accent to-purple-accent text-white shadow-[var(--shadow-large)] hover:shadow-[var(--shadow-hover)] hover:translate-y-[-4px] hover:scale-[1.02] px-8 py-6 text-lg font-bold rounded-lg min-h-[44px]",
      },
      size: {
        default: "h-11 px-6 py-2 text-sm min-h-[44px]",
        sm: "h-10 px-5 text-sm min-h-[40px]",
        lg: "h-14 px-8 text-base min-h-[48px]",
        icon: "h-11 w-11 min-h-[44px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
